import { useMutation } from '@apollo/client';
import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ButtonDock } from '@focuslee/shared-mini-app';
import {
  CreateCategoryMutation,
  CreateCategoryMutationVariables,
  UpdateCategoryMutation,
  UpdateCategoryMutationVariables,
} from '../client/generated/graphql';
import { createCategoryCache, createCategoryMutation } from '../client/mutations/createCategory';
import { updateCategoryCache, updateCategoryMutation } from '../client/mutations/updateCategory';
import { CategoryCreateEditSkeleton } from '../components/category/skeleton/CategoryCreateEditSkeleton';
import { CategorySelect } from '../components/common/CategorySelect';
import { ColorPicker } from '../components/common/ColorPicker';
import { ColorPickerSettings } from '../components/common/ColorPickerSettings';
import { CommonIcon } from '../components/common/CommonIcon';
import { CustomInput } from '../components/common/CustomInput';
import { IconPicker } from '../components/common/IconPicker';
import { IconPickerSettings } from '../components/common/IconPickerSettings';
import { Switch } from '../components/common/Switch';
import { Page } from '../components/Page';
import { AnalyticsEventType } from '../contexts/analyticsEvents';
import { useFeatureFlag } from '../contexts/FeatureFlagContext';
import { useVoice } from '../contexts/VoiceContext';
import { getColor, getRandomColor, resolveColorFromKeyword } from '../helpers/color';
import { getRandomIcon } from '../helpers/common';
import { getButtonThemeColors } from '../helpers/theme';
import { useCachedCategoriesWithFallback } from '../hooks/caching/category';
import { useCachedGoalsWithFallback } from '../hooks/caching/goal';
import { useAnalytics } from '../hooks/useAnalytics';

export const CategoryCreateEditPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { categoryId } = useParams<{ categoryId?: string }>();
  const [searchParams] = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const hasAutoFocusedRef = useRef(false);

  const goalsEnabled = useFeatureFlag('goals');
  const { categories, loading: categoriesLoading } = useCachedCategoriesWithFallback();
  const { goals, loading: goalsLoading } = useCachedGoalsWithFallback();
  
  const editingCategory = useMemo(() => 
    categoryId ? categories.find(c => c.id === categoryId) : undefined,
    [categoryId, categories]
  );
  const editingGoal = useMemo(() => 
    categoryId ? goals.find(g => g.categoryId === categoryId) : undefined,
    [categoryId, goals]
  );

  const fromGoals = searchParams.get('fromGoals') === 'true';
  const voiceActionId = searchParams.get('voiceActionId');
  const { pendingActions, onUpdateVoiceAction } = useVoice();
  const voiceAction = useMemo(
    () => (voiceActionId ? pendingActions.find((a) => a.id === voiceActionId) : null),
    [voiceActionId, pendingActions]
  );
  const [name, setName] = useState<string>(editingCategory?.name || '');
  const [description, setDescription] = useState<string>(editingCategory?.description || '');
  const [icon, setIcon] = useState<string>(editingCategory?.icon || getRandomIcon());
  const [iconColor, setIconColor] = useState<string>(editingCategory?.iconColor || getRandomColor());
  const [parentId, setParentId] = useState<string>(editingCategory?.parentId || '');
  const [goalEnabled, setGoalEnabled] = useState<boolean>(editingGoal?.enabled || fromGoals);
  const [showIconSettings, setShowIconSettings] = useState(false);
  const [showColorSettings, setShowColorSettings] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const [createCategory, { loading: isCreateLoading }] = useMutation<CreateCategoryMutation, CreateCategoryMutationVariables>(createCategoryMutation, {
    update: createCategoryCache,
  });
  const [updateCategory, { loading: isUpdateLoading }] = useMutation<UpdateCategoryMutation, UpdateCategoryMutationVariables>(updateCategoryMutation, {
    update: updateCategoryCache,
  });

  const isValid = !!name && !!icon && !!iconColor;
  const isLoading = isCreateLoading || isUpdateLoading;
  const isDataLoading = categoriesLoading || (goalsEnabled && goalsLoading) || (categoryId && !editingCategory);
  const { recordEvent } = useAnalytics();

  const navigateBack = useCallback(() => navigate(-1), []);

  const handleInputFocus = useCallback(() => {
    setIsInputFocused(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    requestAnimationFrame(() => {
      const activeElement = document.activeElement as HTMLElement | null;
      if (!activeElement || activeElement.tagName !== 'INPUT') {
        setIsInputFocused(false);
      }
    });
  }, []);

  const handleIconClick = useCallback(() => {
    setShowIconSettings(true);
  }, []);

  const handleSaveIconSettings = useCallback(async (settings: { icon: string }) => {
    setIcon(settings.icon);
    setShowIconSettings(false);
    setIsInputFocused(false);
  }, []);

  const handleCancelIconSettings = useCallback(() => {
    setShowIconSettings(false);
    setIsInputFocused(false);
  }, []);

  const handleColorClick = useCallback(() => {
    setShowColorSettings(true);
  }, []);

  const handleSaveColorSettings = useCallback(async (settings: { color: string }) => {
    setIconColor(settings.color);
    setShowColorSettings(false);
    setIsInputFocused(false);
  }, []);

  const handleCancelColorSettings = useCallback(() => {
    setShowColorSettings(false);
    setIsInputFocused(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!isValid || isLoading) return;
    if (voiceActionId && voiceAction?.type === 'CREATE_CATEGORY') {
      onUpdateVoiceAction(voiceAction.id, {
        displayTitle: name,
        categoryName: name,
        description,
        iconName: icon,
        iconColorName: iconColor,
      });
      navigateBack();
      return;
    }
    try {
      if (categoryId) {
        await updateCategory({ 
          variables: { 
            categoryId, 
            name, 
            description,
            icon, 
            iconColor, 
            parentId: parentId || null, 
            goalEnabled: goalsEnabled ? goalEnabled : false 
          } 
        });
        recordEvent(AnalyticsEventType.CATEGORY_UPDATED, { categoryId });
      } else {
        const result = await createCategory({ 
          variables: { 
            name, 
            description,
            icon, 
            iconColor, 
            parentId: parentId || null, 
            goalEnabled: goalsEnabled ? goalEnabled : false 
          } 
        });
        recordEvent(AnalyticsEventType.CATEGORY_CREATED, { categoryId: result.data?.createCategory?.category?.id });
      }
      navigateBack();
    } catch (err) {
      console.error('Error saving category', err);
    }
  }, [isValid, isLoading, categoryId, name, description, icon, iconColor, parentId, goalEnabled, goalsEnabled, recordEvent, voiceActionId, voiceAction, onUpdateVoiceAction, navigateBack]);

  const saveButtonText = voiceActionId ? t('achievementEditor.editAction') : categoryId ? t('actions.save') : t('actions.create');

  useEffect(() => {
    if (voiceAction?.type === 'CREATE_CATEGORY') {
      setName(voiceAction.categoryName);
      setDescription(voiceAction.description ?? '');
      if (voiceAction.iconName) {
        setIcon(voiceAction.iconName);
      }
      if (voiceAction.iconColorName) {
        setIconColor(resolveColorFromKeyword(voiceAction.iconColorName));
      }
    }
  }, [voiceAction]);

  useEffect(() => {
    if (editingCategory) {
      setName(editingCategory.name);
      setDescription(editingCategory.description || '');
      setIcon(editingCategory.icon);
      setIconColor(editingCategory.iconColor);
      setParentId(editingCategory.parentId || '');
    }
  }, [editingCategory]);

  useEffect(() => {
    if (editingGoal) {
      setGoalEnabled(editingGoal.enabled);
    }
  }, [editingGoal]);

  useEffect(() => {
    if (
      inputRef.current &&
      !editingCategory &&
      !voiceActionId &&
      !showIconSettings &&
      !showColorSettings &&
      !hasAutoFocusedRef.current
    ) {
      inputRef.current.focus();
      hasAutoFocusedRef.current = true;
    }
  }, [editingCategory, voiceActionId, showIconSettings, showColorSettings]);

  const parentCategoryOptions = useMemo(() => [
    { id: '', name: t('categoryEditor.noParent'), icon: '', iconColor: '' },
    ...categories.filter(c => !c.parentId && c.id !== categoryId && !c.system)
  ], [categories, categoryId, t]);

  if (isDataLoading)
    return <CategoryCreateEditSkeleton isEdit={!!categoryId} />;

  if (showIconSettings) {
    return (
      <IconPickerSettings
        initialIcon={icon}
        onSave={handleSaveIconSettings}
        onCancel={handleCancelIconSettings}
      />
    );
  }

  if (showColorSettings) {
    return (
      <ColorPickerSettings
        initialColor={iconColor}
        onSave={handleSaveColorSettings}
        onCancel={handleCancelColorSettings}
      />
    );
  }

  return (
    <Page
      back
      insetEnabled={false}
      footer={
        <ButtonDock
          secondary={{ text: t('actions.cancel'), onClick: navigateBack, navigation: 'back' }}
          main={{
            text: saveButtonText,
            onClick: handleSave,
            enabled: isValid && !isLoading,
            loading: isLoading,
            bgColor: getButtonThemeColors(isValid && !isLoading).backgroundColor,
          }}
        />
      }
    >
      <Stack alignItems="center" justifyContent="center" minHeight="var(--tg-viewport-content-safe-area-inset-top)">
        <Text>{voiceActionId ? t('achievementEditor.editAction') : categoryId ? t('category.edit') : t('category.create')}</Text>
      </Stack>
      <Stack 
        height="100%" 
        paddingTop="10px" 
        paddingLeft="10px" 
        paddingRight="10px" 
        rowGap="15px"
        sx={{ 
          overflow: isInputFocused ? 'hidden' : 'visible',
          maxHeight: isInputFocused ? '70vh' : 'none'
        }}
      >
        {!isInputFocused && (
          <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
            <Box sx={{
              width: 60,
              height: 60,
              borderRadius: 2,
              background: getColor(iconColor),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CommonIcon
                icon={icon}
                type="filled"
                size={50}
                color="var(--tg-theme-text-color)"
              />
            </Box>
          </Stack>
        )}
        <CustomInput
          value={name}
          onChange={(e) => setName(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          ref={inputRef}
          style={{ fontSize: '20px', lineHeight: '20px' }}
          wrapperStyle={{ minHeight: '0px' }}
          placeholder={t('categoryEditor.name')}
        />
        <CustomInput
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={t('categoryEditor.description')}
          wrapperStyle={{ minHeight: '0px' }}
        />
        <IconPicker
          selectedIcon={icon}
          selectedIconColor={iconColor}
          onClick={handleIconClick}
        />
        <ColorPicker
          selectedColor={iconColor}
          onClick={handleColorClick}
        />
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" gap={3}>
          <CategorySelect
            categoryId={parentId}
            setCategoryId={setParentId}
            categories={parentCategoryOptions}
            renderSelected={(c) => c && c.id !== '' ? c.name : t('categoryEditor.parent')}
            renderSelectedBefore={(c) => <CommonIcon icon={c && c.id !== '' ? c.icon : "account_tree"} type="filled" color={c && c.id !== '' ? getColor(c.iconColor) : "var(--tg-theme-text-color)"} size={20} />}
          />
          {goalsEnabled && (
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="50%">
              <Stack direction="row" alignItems="center" gap={1}>
                <Stack>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <CommonIcon icon="flag" type="filled" color="var(--tg-theme-text-color)" size={20} />
                    <Text>{t('categoryEditor.goal')}</Text>
                  </Stack>
                  <Text style={{ fontSize: '12px', color: 'var(--tg-theme-hint-color)' }}>{t('categoryEditor.enableGoal')}</Text>
                </Stack>
              </Stack>
              <Switch checked={goalEnabled} onChange={setGoalEnabled} disabled={fromGoals} />
            </Stack>
          )}
        </Stack>
      </Stack>
    </Page>
  );
};
